import React, { useState, KeyboardEvent, useRef } from 'react';
import { get } from '../../../hook/request';

export type UtenteSearch = {
  ID: string;
  nome: string;
  cognome: string;
  email?: string;
};

type Props = {
  selectedUsers: { ID?: string; nominativo?: string; email?: string }[];
  onAddUser: (u: UtenteSearch) => void;
  onRemoveUser: (id: string) => void;
};

export default function UserAutocomplete({ selectedUsers, onAddUser, onRemoveUser }: Props) {
  const [userQuery, setUserQuery] = useState('');
  const [userSuggestions, setUserSuggestions] = useState<UtenteSearch[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleUserQueryChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserQuery(value);
    if (value.length < 2) {
      setUserSuggestions([]);
      setOpen(false);
      return;
    }

    try {
      setSearchingUsers(true);
      const res = await get<UtenteSearch[] | { data?: UtenteSearch[] }>(
        `/users/search?temp=${encodeURIComponent(value)}`
      );
      const list = (res as any)?.data ?? res;
      setUserSuggestions(Array.isArray(list) ? list : []);
      setOpen(true);
    } catch (err) {
      console.error('Errore nella ricerca utenti', err);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleSelectSuggestion = (u: UtenteSearch) => {
    onAddUser(u);
    setUserQuery('');
    setUserSuggestions([]);
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && open && userSuggestions.length > 0) {
      e.preventDefault();
      handleSelectSuggestion(userSuggestions[0]);
    }

    if (e.key === 'Backspace' && userQuery === '' && selectedUsers.length > 0) {
      const last = selectedUsers[selectedUsers.length - 1];
      if (last.ID) {
        onRemoveUser(last.ID);
      }
    }
  };

  return (
    <div className="position-relative">
      <div
        className="form-control form-control-sm d-flex flex-wrap align-items-center gap-1"
        style={{ minHeight: '31px', paddingTop: 2, paddingBottom: 2 }}
        onClick={() => {
          inputRef.current?.focus();   
        }}
      >
        {selectedUsers?.map(u => (
          <span
            key={u.ID}
            className="badge bg-secondary d-inline-flex align-items-center gap-1"
          >
            {u.nominativo}
            {u.ID && (
              <button
                type="button"
                className="btn-close btn-close-white btn-sm ms-1"
                aria-label="Rimuovi"
                onClick={e => {
                  e.stopPropagation();
                  onRemoveUser(u.ID!);
                }}
                style={{ fontSize: '0.6rem' }}
              />
            )}
          </span>
        ))}

        <input
          ref={inputRef}                  
          type="text"
          className="border-0 flex-grow-1"
          style={{ minWidth: 60, outline: 'none' }}
          placeholder={selectedUsers.length === 0 ? 'Cerca utente...' : ''}
          value={userQuery}
          onChange={handleUserQueryChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open && (searchingUsers || userSuggestions.length > 0) && (
        <ul
          className="list-group position-absolute w-100 mt-1"
          style={{ zIndex: 1000 }}
        >
          {searchingUsers && (
            <li className="list-group-item small text-muted">Ricerca...</li>
          )}
          {userSuggestions.map(u => (
            <li
              key={u.ID}
              className="list-group-item list-group-item-action"
              onClick={() => handleSelectSuggestion(u)}
              style={{ cursor: 'pointer' }}
            >
              {u.nome} {u.cognome}
              {u.email && <span className="text-muted"> ({u.email})</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
